import axios from 'axios'
import './axiosConfig'

const API_BASE_URL = import.meta.env.VITE_API_URL

// A row of Settings > Organization > Users and permissions. The Gateway leaves out every site
// and permission group the signed-in user does not hold, and every user who holds none of those
// sites. `manageable` is false for a user who holds more than the signed-in user.
export type ManagedUser = {
  userId: number
  userName: string
  displayName: string
  email: string | null
  phone: string | null
  active: boolean
  siteIds: number[]
  roleIds: number[]
  manageable: boolean
  self: boolean
}

// A permission group (auth.roles). siteId is null for a group of every site.
export type PermissionGroup = {
  roleId: number
  name: string
  siteId: number | null
}

export type UserOptions = {
  sites: { siteId: number; siteName: string }[]
  roles: PermissionGroup[]
  // False for a signed-in user outside rimonim.tech. The Gateway decides; the screen only reflects it.
  canEditUsers: boolean
}

// What the form sends. userName is read only on create; on edit a password resets it.
export type UserForm = {
  userName?: string
  displayName: string
  email: string
  phone: string
  siteIds: number[]
  roleIds: number[]
  password?: string
  // The password of the signed-in user. The Gateway asks for it when the e-mail or the phone changes.
  callerPassword?: string
}

const USERS = () => `${API_BASE_URL}/api/users`

export const listUsers = async (): Promise<ManagedUser[]> =>
  (await axios.get(USERS())).data.data ?? []

export const getUserOptions = async (): Promise<UserOptions> => {
  const data = (await axios.get(`${USERS()}/options`)).data.data
  return {
    sites: data?.sites ?? [],
    roles: data?.roles ?? [],
    canEditUsers: data?.canEditUsers === true,
  }
}

export const createUser = async (form: UserForm): Promise<ManagedUser> =>
  (await axios.post(USERS(), form)).data.data

export const updateUser = async (
  userId: number,
  form: UserForm
): Promise<ManagedUser> =>
  (await axios.put(`${USERS()}/${userId}`, form)).data.data

export const setUserActive = async (
  userId: number,
  active: boolean
): Promise<ManagedUser> =>
  (await axios.put(`${USERS()}/${userId}/active`, { active })).data.data

export const deleteUser = async (userId: number): Promise<void> => {
  await axios.delete(`${USERS()}/${userId}`)
}

export const NO_PERMISSION_TEXT = 'אין לך הרשאה לבצע פעולה זו'

// The Gateway's error codes, in the words the screen shows. Any other failure gets the fallback.
const ERROR_TEXT: Record<string, string> = {
  USER_NAME_TAKEN: 'שם המשתמש כבר קיים במערכת',
  SITE_REMOVAL_DENIED: 'עדיין אי אפשר להסיר אתר ממשתמש. אפשר להוסיף אתרים בלבד',
  NOT_PERMITTED: NO_PERMISSION_TEXT,
  FORBIDDEN: 'אין לך הרשאה לבצע את הפעולה הזו',
  USER_NOT_FOUND: 'המשתמש לא נמצא. ייתכן שנמחק',
  INVALID: 'אחד השדות אינו תקין',
  PASSWORD_CHECK_FAILED:
    'הסיסמה שלך שגויה. כדי לשנות מייל או טלפון יש להזין את הסיסמה שלך',
  PASSWORD_REUSED: 'הסיסמה החדשה שימשה בעבר. נא לבחור סיסמה אחרת',
}

export const userAdminErrorText = (error: unknown, fallback: string) => {
  const code = axios.isAxiosError(error)
    ? error.response?.data?.error
    : undefined
  return (code && ERROR_TEXT[code]) || fallback
}

// The Gateway stores E.164. An Israeli number shows as the local 050-1234567.
export const formatPhone = (phone: string | null) => {
  if (!phone) return ''
  const local = /^\+972(\d{2})(\d{7})$/.exec(phone)
  return local ? `0${local[1]}-${local[2]}` : phone
}
